import type { PriceProps } from '@maestro/commerce-blocks/components/price/price.types'

import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { useMaestroSession } from '@maestro/commerce-blocks/providers/maestro-session/maestro-session.context'
import {
  divideAmount,
  formatWithThousandSeparator,
} from '@maestro/commerce-blocks/utils/price.utils'
import { theme } from '@maestro/local/theme'

export const usePrice = ({
  price,
  prefix,
  suffix,
  forceShowDecimals = undefined,
  isActive = true,
  isDiscountedPrice = false,
  isMemberPrice = false,
  isDecimalSuper = false,
  isNegativeNumber = false,
  unitLabel,
  accessibleLabel,
  showTaxLabel,
}: PriceProps) => {
  const { priceConfig } = theme()
  const { session } = useMaestroSession()
  const t = useTranslations('commerce-blocks.price')

  const isPriceVisible = useMemo(() => {
    const isLoggedIn = !!session
    return !priceConfig.showOnlyForLoggedIn || isLoggedIn
  }, [priceConfig.showOnlyForLoggedIn, session])

  const { integerPrice: integerPriceProps, decimalPrice: decimalPriceProps } =
    useMemo(() => {
      const priceForCalculations =
        (isMemberPrice ? price?.memberAmount : undefined) ??
        (isDiscountedPrice ? price?.discountedAmount : undefined) ??
        price?.amount
      return divideAmount(priceForCalculations ?? 0)
    }, [
      isDiscountedPrice,
      isMemberPrice,
      price?.amount,
      price?.discountedAmount,
      price?.memberAmount,
    ])

  const integerPrice = useMemo(() => {
    let integer = integerPriceProps
    if (!decimalPriceProps) {
      const parts = `${integerPriceProps}`.split(/[.,]/)
      integer = parseInt(parts[0], 10)
    }

    const finalInteger = isNegativeNumber ? -integer : integer

    // Apply thousand separator formatting if enabled
    if (priceConfig.showThousandSeparator && Math.abs(finalInteger) >= 1000) {
      return formatWithThousandSeparator(
        finalInteger,
        priceConfig.thousandSeparator
      )
    }

    return Object.is(finalInteger, -0) ? '-0' : String(finalInteger)
  }, [decimalPriceProps, integerPriceProps, isNegativeNumber, priceConfig])

  const decimalPrice = useMemo(() => {
    const content = `${priceConfig.separator}${decimalPriceProps}`
    if (forceShowDecimals !== undefined) {
      return forceShowDecimals ? content : ''
    }
    return priceConfig.showDecimals ? content : ''
  }, [decimalPriceProps, forceShowDecimals, priceConfig])

  const isDecimalZero = useMemo(() => {
    return decimalPrice === `${priceConfig.separator}00`
  }, [decimalPrice, priceConfig.separator])

  const isTaxLabelVisible = useMemo(() => {
    return (showTaxLabel ?? priceConfig.showTaxLabel) && isActive
  }, [showTaxLabel, priceConfig.showTaxLabel, isActive])

  const taxLabel = useMemo(() => {
    return isTaxLabelVisible ? t('ex-vat') : ''
  }, [isTaxLabelVisible, t])

  const formattedPrice = priceConfig.showFormattedPrice
    ? price?.formattedValue
    : undefined

  const screenReaderText = useMemo(() => {
    const priceText = formattedPrice
      ? `${formattedPrice}${unitLabel || ''}${taxLabel ? ` ${taxLabel}` : ''}.`
      : `${integerPrice}${decimalPrice || ''}${unitLabel || ''} ${priceConfig.currencyName}${taxLabel ? ` ${taxLabel}` : ''}.`
    return accessibleLabel ? `${accessibleLabel}: ${priceText}` : priceText
  }, [
    accessibleLabel,
    decimalPrice,
    formattedPrice,
    integerPrice,
    priceConfig.currencyName,
    taxLabel,
    unitLabel,
  ])

  return {
    isPriceVisible,
    integerPrice,
    decimalPrice,
    prefix: prefix ?? priceConfig.prefix,
    suffix: suffix ?? priceConfig.suffix,
    isActive,
    isDiscountedPrice,
    isMemberPrice,
    isDecimalSuper,
    isDecimalZero,
    unitLabel,
    taxLabel,
    screenReaderText,
    formattedPrice,
  }
}
